import React from 'react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import { SectionHeading, SubHeading, Para, Mark, Code, Callout, Output, Pill, TwoUp, QA } from './articleKit';

type Author = { name: string; fullName: string; avatar?: string };

export const JSDomManipulation: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">DOM Manipulation</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    Selectors find elements; DOM manipulation changes them. Reading, creating, updating, deleting, and styling nodes — plus the performance and security costs that come with it.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="20 min read" />
  </div>

  {/* 1. The Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>On Amazon, Add To Cart updates the cart count, searching changes the products, logging in changes the navbar. How does a page change after it has loaded?</Para>
   <Para>HTML alone can&apos;t do this. JavaScript must create, update, delete, move, and style elements. That process is <Mark>DOM manipulation</Mark>.</Para>
  </section>

  {/* 2. What Is DOM Manipulation */}
  <section className="mb-12">
   <SectionHeading n={2}>What Is DOM Manipulation?</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200"><Mark>DOM manipulation</Mark> means reading, creating, updating, deleting, and styling DOM elements dynamically with JavaScript.</p>
   </div>
   <Para>HTML is the building blueprint; DOM manipulation is the renovation work — you keep modifying the building after it&apos;s built.</Para>
  </section>

  {/* 3. Reading Text Content */}
  <section className="mb-12">
   <SectionHeading n={3}>Reading Text Content</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `// <h1 id="title">Welcome</h1>\nconst title = document.getElementById("title");\nconsole.log(title.textContent);` }]} defaultLanguage="javascript" id="dm-readtext" />
   <Output>{`Welcome`}</Output>
   <Para>Most used for labels, messages, titles, and counters.</Para>
  </section>

  {/* 4. Reading HTML */}
  <section className="mb-12">
   <SectionHeading n={4}>Reading HTML</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `// <div><h1>Hello</h1></div>\nconsole.log(element.innerHTML); // "<h1>Hello</h1>"` }]} defaultLanguage="javascript" id="dm-readhtml" />
   <Para>Returns the entire HTML inside the element as a string.</Para>
  </section>

  {/* 5. textContent */}
  <section className="mb-12">
   <SectionHeading n={5}>Updating with textContent</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `title.textContent = "Hello Sachin"; // <h1>Hello Sachin</h1>` }]} defaultLanguage="javascript" id="dm-text" />
   <Para>It&apos;s the safest content update — everything is treated as plain text:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `title.textContent = "<script>alert(1)</script>";\n// renders the literal text — no script runs` }]} defaultLanguage="javascript" id="dm-text-safe" />
  </section>

  {/* 6. innerHTML */}
  <section className="mb-12">
   <SectionHeading n={6}>Updating with innerHTML</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `container.innerHTML = \`<h1>Hello</h1>\`; // browser creates real HTML` }]} defaultLanguage="javascript" id="dm-html" />
   <Para>Useful for templates, cards, lists, and tables.</Para>
  </section>

  {/* 7. innerHTML vs textContent */}
  <section className="mb-12">
   <SectionHeading n={7}>innerHTML vs textContent (Most Asked)</SectionHeading>
   <TwoUp
    left={{ title: 'innerHTML', items: ['Parses HTML', 'element.innerHTML = "<b>Hello</b>"', '→ bold "Hello"'] }}
    right={{ title: 'textContent', items: ['Plain text only', 'element.textContent = "<b>Hello</b>"', '→ literal <b>Hello</b>'] }}
   />
   <Para>Interview answer: <Mark>innerHTML creates HTML; textContent creates text.</Mark></Para>
  </section>

  {/* 8. createElement */}
  <section className="mb-12">
   <SectionHeading n={8}>createElement()</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const button = document.createElement("button"); // exists in memory only, not visible yet` }]} defaultLanguage="javascript" id="dm-create" />
  </section>

  {/* 9 & 10. Setting content + attributes */}
  <section className="mb-12">
   <SectionHeading n={9}>Setting Content &amp; Attributes</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `button.textContent = "Login";\nbutton.id = "loginBtn";\nbutton.className = "btn-primary";\n// element now fully built` }]} defaultLanguage="javascript" id="dm-setcontent" />
  </section>

  {/* 11. appendChild */}
  <section className="mb-12">
   <SectionHeading n={11}>appendChild()</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `document.body.appendChild(button); // now visible` }]} defaultLanguage="javascript" id="dm-append" />
   <CodeBlock snippets={[{ language: 'text', code: `memory → DOM → browser paint` }]} defaultLanguage="text" id="dm-append-flow" />
  </section>

  {/* 12. append */}
  <section className="mb-12">
   <SectionHeading n={12}>append()</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `container.append(button); // modern API` }]} defaultLanguage="javascript" id="dm-appendmodern" />
   <Para>Can append text, elements, and multiple nodes at once (and returns nothing, unlike <Code>appendChild</Code>).</Para>
  </section>

  {/* 13. prepend */}
  <section className="mb-12">
   <SectionHeading n={13}>prepend()</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `container.prepend(button); // adds at the beginning` }]} defaultLanguage="javascript" id="dm-prepend" />
   <Para>Useful for newest notifications, chats, and comments.</Para>
  </section>

  {/* 14. remove */}
  <section className="mb-12">
   <SectionHeading n={14}>remove()</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `button.remove();` }]} defaultLanguage="javascript" id="dm-remove" />
   <Para>Production: delete product, remove chat, dismiss notification.</Para>
  </section>

  {/* 15. removeChild */}
  <section className="mb-12">
   <SectionHeading n={15}>removeChild()</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `parent.removeChild(child); // older API, still in legacy code` }]} defaultLanguage="javascript" id="dm-removechild" />
  </section>

  {/* 16-18. Attributes */}
  <section className="mb-12">
   <SectionHeading n={16}>Attributes: get / set / remove</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `element.getAttribute("data-id");          // read\nelement.setAttribute("data-id", "101");   // write\nelement.removeAttribute("disabled");      // remove` }]} defaultLanguage="javascript" id="dm-attr" />
   <Para>Common production attributes: <Code>data-id</Code>, <Code>data-user</Code>, <Code>data-order</Code>.</Para>
  </section>

  {/* 19. Inline styles */}
  <section className="mb-12">
   <SectionHeading n={19}>CSS: Inline Styles</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `element.style.color = "red";\nelement.style.fontSize = "20px";` }]} defaultLanguage="javascript" id="dm-style" />
   <Callout tone="warning" title="Use sparingly">Inline styles work but are hard to maintain — prefer class toggling.</Callout>
  </section>

  {/* 20-22. classList add/remove/toggle */}
  <section className="mb-12">
   <SectionHeading n={20}>Class Manipulation: add / remove / toggle</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `element.classList.add("active");\nelement.classList.remove("active");\nelement.classList.toggle("active");` }]} defaultLanguage="javascript" id="dm-classlist" />
   <Para>The most used DOM API in production. Example: open modal → add <Code>active</Code> class → modal visible.</Para>
  </section>

  {/* 23. classList.contains */}
  <section className="mb-12">
   <SectionHeading n={23}>classList.contains()</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `element.classList.contains("active"); // true / false` }]} defaultLanguage="javascript" id="dm-contains" />
  </section>

  {/* 24. Dark Mode */}
  <section className="mb-12">
   <SectionHeading n={24}>Real Example: Dark Mode</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `document.body.classList.toggle("dark-mode");` }]} defaultLanguage="javascript" id="dm-dark" />
   <Para>Simple, powerful, and widely used.</Para>
  </section>

  {/* 25. The Hidden Cost */}
  <section className="mb-12">
   <SectionHeading n={25}>Performance: The Hidden Cost</SectionHeading>
   <Para>Every DOM update can trigger browser work — <Mark>layout, paint, composite</Mark>. Updating the DOM is expensive.</Para>
  </section>

  {/* 26. Reflow */}
  <section className="mb-12">
   <SectionHeading n={26}>Reflow</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `element.style.width = "500px"; // browser recomputes positions, sizes, layout` }]} defaultLanguage="javascript" id="dm-reflow" />
   <Para>Recalculating layout is called <Mark>reflow</Mark> — it&apos;s expensive.</Para>
  </section>

  {/* 27. Repaint */}
  <section className="mb-12">
   <SectionHeading n={27}>Repaint</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `element.style.color = "red"; // layout unchanged → repaint only` }]} defaultLanguage="javascript" id="dm-repaint" />
   <Callout tone="info" title="Interview favourite">Reflow &gt; repaint in cost. A visual-only change repaints; a layout change reflows <em>and</em> repaints.</Callout>
  </section>

  {/* 28. Batch Updates (bad) */}
  <section className="mb-12">
   <SectionHeading n={28}>The Batching Problem</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `for (let i = 0; i < 1000; i++) {\n  container.innerHTML += \`<li>\${i}</li>\`; // ❌ ~1000 re-renders\n}` }]} defaultLanguage="javascript" id="dm-bad" />
  </section>

  {/* 29. DocumentFragment */}
  <section className="mb-12">
   <SectionHeading n={29}>DocumentFragment</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const fragment = document.createDocumentFragment();\n\nfor (let i = 0; i < 1000; i++) {\n  const item = document.createElement("li");\n  item.textContent = i;\n  fragment.appendChild(item);\n}\n\ncontainer.appendChild(fragment); // ✅ one DOM update` }]} defaultLanguage="javascript" id="dm-fragment" />
   <Para>Build everything off-DOM, then attach once — a huge performance improvement.</Para>
  </section>

  {/* 30. Security / XSS */}
  <section className="mb-12">
   <SectionHeading n={30}>Security: The Biggest Danger</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `element.innerHTML = userInput;\n// if userInput = "<script>alert(1)</script>", the browser may execute it` }]} defaultLanguage="javascript" id="dm-xss" />
   <Callout tone="warning" title="XSS — Cross-Site Scripting">Assigning untrusted input to <Code>innerHTML</Code> can run attacker code.</Callout>
  </section>

  {/* 31. Safer Alternative */}
  <section className="mb-12">
   <SectionHeading n={31}>Safer Alternative</SectionHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `element.textContent = userInput; // safe — treated as plain text` }]} defaultLanguage="javascript" id="dm-safe" />
   <Para>Use <Code>textContent</Code> for user-generated content (or sanitize before using <Code>innerHTML</Code>).</Para>
  </section>

  {/* 32. Modern Framework Reality */}
  <section className="mb-12">
   <SectionHeading n={32}>Modern Framework Reality</SectionHeading>
   <Para>In React you rarely call <Code>appendChild()</Code> directly. React maintains a <Mark>Virtual DOM</Mark> and performs efficient DOM manipulation for you — but under the hood it still manipulates the real DOM.</Para>
  </section>

  {/* 33-38. Common Interview Questions */}
  <section className="mb-12">
   <SectionHeading n={33}>Common Interview Questions</SectionHeading>
   <div className="flex flex-wrap gap-2 mb-5">
    {['innerHTML vs textContent?', 'append vs appendChild?', 'What is DocumentFragment?', 'What causes reflow/repaint?', 'How can DOM manipulation create XSS?', 'Why is classList preferred over style?'].map((q) => <Pill key={q}>{q}</Pill>)}
   </div>
   <QA q="What is the difference between append and appendChild?">
    <Code>appendChild</Code> accepts a single <em>node</em> and returns it. <Code>append</Code> (newer) accepts multiple nodes <em>and</em> strings, and returns nothing. <Code>append</Code> is more flexible; <Code>appendChild</Code> has slightly broader legacy support.
   </QA>
   <QA q="What is a DocumentFragment and why use it?">
    A lightweight, off-screen container for DOM nodes. You build your subtree inside it (no reflows), then append the fragment once — turning many DOM updates into a single one for a big performance win.
   </QA>
   <QA q="What's the difference between reflow and repaint?">
    Reflow recalculates layout (positions/sizes) and is expensive; repaint only redraws visual changes like color with no layout change. Layout-affecting updates trigger both; visual-only updates trigger just a repaint.
   </QA>
   <QA q="Why is classList preferred over inline style?">
    Class toggling keeps styling in CSS (maintainable, themeable, cacheable) and can change many properties at once, while inline styles scatter presentation through JS and are harder to override and maintain.
   </QA>
  </section>

  {/* 39. Real Production Example */}
  <section className="mb-12">
   <SectionHeading n={39}>Real Production Example: Shopping Cart</SectionHeading>
   <CodeBlock snippets={[{ language: 'text', code: `Click Add To Cart →\n  create cart item → append item → update count → add animation class → repaint UI` }]} defaultLanguage="text" id="dm-cart" />
   <Para>Every step here is DOM manipulation working together.</Para>
  </section>

  {/* Interview One-Liner */}
  <section className="mb-12">
   <SectionHeading n={40}>Interview One-Liner</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4">
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     DOM manipulation is the process of dynamically creating, updating, deleting, styling, and restructuring DOM elements using JavaScript.
    </p>
   </div>
  </section>

  {/* Most Important Takeaway */}
  <section className="mb-4">
   <SectionHeading n={41}>Most Important Takeaway</SectionHeading>
   <Callout tone="info" title="Takeaway">
    Selectors find elements; DOM manipulation changes them. Understanding <Code>textContent</Code>, <Code>innerHTML</Code>, <Code>createElement</Code>, <Code>appendChild</Code>, <Code>classList</Code>, attributes, <Code>DocumentFragment</Code>, reflow, repaint, and XSS is essential for frontend development, browser internals, machine-coding rounds, and framework understanding.
   </Callout>
  </section>
 </div>
);

export default JSDomManipulation;
